import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { SITE } from "../data/site";

const EASE = [0.76, 0, 0.24, 1];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.5 } },
};
const lineV = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1.1, ease: EASE } },
};

const Line = ({ children }) => (
  <span className="block overflow-hidden">
    <motion.span variants={lineV} className="block">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const markY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const markOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero"
      className="relative min-h-[100svh] w-full flex flex-col justify-between overflow-hidden pt-28 md:pt-24 pb-10"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 42%, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0) 70%)",
        }}
      />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1, duration: 1 }}
        className="mx-auto w-full max-w-[1600px] px-5 md:px-10 flex items-center justify-between font-mono text-[10px] md:text-[11px] tracking-[0.25em] uppercase text-[var(--fg-dim)]"
      >
        <span>Est. 2026</span>
        <span className="hidden sm:block">Pizza · Birra · Smash Burger</span>
        <span>45.3394° N — 9.9262° E</span>
      </motion.div>

      <div className="relative mx-auto w-full max-w-[1600px] px-5 md:px-10 flex-1 flex flex-col items-center justify-center text-center">
        <motion.img
          src="/assets/mark-white.png"
          alt="Stemma Dal Conte"
          style={{ y: markY, opacity: markOpacity }}
          initial={{ opacity: 0, scale: 0.86, filter: "blur(8px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 1.4, ease: EASE, delay: 0.1 }}
          className="h-24 md:h-36 lg:h-44 w-auto mb-6 md:mb-10"
        />

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          className="font-serif leading-[0.92] tracking-tight text-[14vw] sm:text-7xl md:text-8xl lg:text-[7.5rem]"
        >
          <Line>Pizza, birra</Line>
          <Line>
            e <span className="italic font-light">smash burger</span>
          </Line>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 1 }}
          className="mt-8 md:mt-10 max-w-md text-[var(--fg-dim)] text-sm md:text-base leading-relaxed"
        >
          La nostra pizza è sottilissima, larga 42 cm e leggera da digerire.
          Accanto, birra e veri smash burger — negli spazi storici di Palazzo
          Martinengo di Villagana.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="mt-9 flex flex-col sm:flex-row items-center gap-4"
        >
          <a
            data-testid="hero-reserve"
            href={SITE.reserveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-[11px] tracking-[0.2em] uppercase bg-[var(--fg)] text-[var(--bg)] px-8 py-4 border border-[var(--fg)] hover:bg-transparent hover:text-[var(--fg)] transition-colors duration-400"
          >
            Prenota un tavolo
          </a>
          <button
            data-testid="hero-menu"
            onClick={() => document.querySelector("#menu")?.scrollIntoView({ behavior: "smooth" })}
            className="font-mono text-[11px] tracking-[0.2em] uppercase px-8 py-4 border border-[var(--line-strong)] text-[var(--fg)] hover:border-[var(--fg)] transition-colors duration-400"
          >
            Scopri il menù
          </button>
        </motion.div>

        <motion.p
          data-testid="opening-date"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.45, duration: 1 }}
          className="mt-8 font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--fg-dim)]"
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--fg)] mr-3 align-middle animate-pulse" />
          Apertura · {SITE.openingDate}
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="mx-auto w-full max-w-[1600px] px-5 md:px-10 flex items-center justify-center gap-3 font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--fg-dim)]"
      >
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        >
          <ArrowDown size={14} strokeWidth={1.4} />
        </motion.span>
        Scorri
      </motion.div>
    </section>
  );
};
